/**
 * TopBar Component
 * Header navigation with search, notifications, and user profile
 */

import React, { useState } from 'react';
import { APP_BAR_HEIGHT } from '../../theme/layoutConstants';
import { useNavigate } from 'react-router-dom';
import LogoutSplash from '../auth/LogoutSplash';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  InputBase,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper,
  useTheme,
  alpha,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Refresh as RefreshIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { logout } from '../../store/authSlice';
import { setTheme, toggleSidebar, clearNotifications } from '../../store/uiSlice';
import { format } from 'date-fns';
import { useHelpContent } from '../../contexts/HelpContentContext';

interface TopBarProps {
  onMenuClick: () => void;
  onSearch?: (query: string) => void;
}

export const TopBar: React.FC<TopBarProps> = ({ onMenuClick, onSearch }) => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  
  const { user } = useSelector((state: RootState) => state.auth);
  const { notifications, theme: currentTheme, breadcrumbs } = useSelector((state: RootState) => state.ui);
  const { openDrawer: openHelpDrawer } = useHelpContent();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showLogoutSplash, setShowLogoutSplash] = useState(false);

  const unreadNotifications = notifications.filter(n => !n.read);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchor(event.currentTarget);
  };

  const handleLogout = async () => {
    setProfileAnchor(null);
    setShowLogoutSplash(true);
    
    try {
      await dispatch(logout()).unwrap();
    } catch (error) {
      console.error('Logout failed:', error);
      // Still show logout splash even if logout fails
    }
  };

  const handleLogoutComplete = () => {
    setShowLogoutSplash(false);
    window.location.href = '/auth/login?provider=google';
  };

  const handleThemeToggle = () => {
    dispatch(setTheme(currentTheme === 'light' ? 'dark' : 'light'));
  };

  const handleFullscreenToggle = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleClearNotifications = () => {
    dispatch(clearNotifications());
    setNotificationAnchor(null);
  };


  const renderBreadcrumbs = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {breadcrumbs.map((crumb, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <Typography color="text.secondary" sx={{ mx: 1 }}>
              /
            </Typography>
          )}
          <Typography
            variant="body2"
            color={index === breadcrumbs.length - 1 ? 'text.primary' : 'text.secondary'}
            sx={{
              fontWeight: index === breadcrumbs.length - 1 ? 600 : 400,
              cursor: crumb.path ? 'pointer' : 'default',
              '&:hover': crumb.path ? { color: 'primary.main' } : {},
            }}
          >
            {crumb.label}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      elevation={1}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: APP_BAR_HEIGHT }}>
        {/* Left Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            edge="start"
            onClick={onMenuClick}
            sx={{ color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>

          {/* Breadcrumbs */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            {renderBreadcrumbs()}
          </Box>
        </Box>

        {/* Center Section - AI Testing Agents + Search */}
        <Box sx={{ flexGrow: 1, maxWidth: 700, mx: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
  

          {/* Search Bar */}
          <Paper
            component="form"
            sx={{
              p: '2px 4px',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: alpha(theme.palette.action.hover, 0.1),
              border: `1px solid ${alpha(theme.palette.action.hover, 0.2)}`,
              '&:focus-within': {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.background.paper,
              },
              flexGrow: 1,
            }}
            elevation={0}
          >
            <IconButton sx={{ p: '10px' }} disabled>
              <SearchIcon />
            </IconButton>
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Search users, settings, logs..."
              inputProps={{ 'aria-label': 'search' }}
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </Paper>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Theme Toggle */}
          <Tooltip title={`Switch to ${currentTheme === 'light' ? 'dark' : 'light'} mode`}>
            <IconButton onClick={handleThemeToggle} sx={{ color: 'text.primary' }}>
              {currentTheme === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>

          {/* Fullscreen Toggle */}
          <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
            <IconButton onClick={handleFullscreenToggle} sx={{ color: 'text.primary' }}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>

          {/* Refresh */}
          <Tooltip title="Refresh data">
            <IconButton onClick={() => window.location.reload()} sx={{ color: 'text.primary' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {/* Help — opens HelpDrawer per Phase 8 Prompt 2 */}
          <Tooltip title="Help — glossary, common tasks, what's new">
            <IconButton
              onClick={() => openHelpDrawer()}
              sx={{ color: 'text.primary' }}
              aria-label="Open help drawer"
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton onClick={handleNotificationClick} sx={{ color: 'text.primary' }}>
              <Badge badgeContent={unreadNotifications.length} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* User Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {user?.name || user?.email || 'Admin User'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.role ? user.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Admin'}
              </Typography>
            </Box>
            <IconButton onClick={handleProfileClick} sx={{ p: 0.5 }}>
              <Avatar
                src={user?.avatar_url}
                alt={user?.name}
                sx={{ width: 36, height: 36 }}
              >
                {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : user?.email?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
          </Box>
        </Box>
      </Toolbar>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={() => setNotificationAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { 
            width: { xs: 'calc(100vw - 32px)', sm: 360 },
            maxWidth: { xs: 'calc(100vw - 32px)', sm: 400 },
            maxHeight: { xs: 'calc(100vh - 100px)', sm: 450 },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        slotProps={{
          paper: {
            style: {
              marginTop: 8,
            }
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Notifications
            </Typography>
            {notifications.length > 0 && (
              <Button size="small" onClick={handleClearNotifications}>
                Clear All
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ overflow: 'auto', flex: 1 }}>
          {notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No notifications</Typography>
            </Box>
          ) : (
            notifications.slice(0, 10).map((notification) => (
              <MenuItem
                key={notification.id}
                sx={{
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  borderBottom: 1,
                  borderColor: 'divider',
                  py: 1.5,
                  px: 2,
                  whiteSpace: 'normal',
                  '&:last-child': { borderBottom: 0 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                  <Chip
                    size="small"
                    label={notification.type}
                    color={
                      notification.type === 'error' ? 'error' :
                      notification.type === 'warning' ? 'warning' :
                      notification.type === 'success' ? 'success' : 'info'
                    }
                    sx={{ mt: 0.5, flexShrink: 0 }}
                  />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                      }}
                    >
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {format(new Date(notification.timestamp), 'MMM dd, HH:mm')}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))
          )}
        </Box>
      </Menu>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { width: 280 },
        }}
      >
        {/* User Info */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={user?.avatar_url}
              alt={user?.name}
              sx={{ width: 48, height: 48 }}
            >
              {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : user?.email?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {user?.name || user?.email || 'Admin User'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
              <Chip
                size="small"
                label={user?.role ? user.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Admin'}
                color="primary"
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Box>
        </Box>

        {/* Menu Items */}
        <MenuItem onClick={() => {
          setProfileAnchor(null);
          navigate('/dashboard/profile');
        }}>
          <ListItemIcon>
            <AccountIcon />
          </ListItemIcon>
          <ListItemText primary="Profile Settings" />
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Sign Out" />
        </MenuItem>
      </Menu>

      {/* Logout Splash Screen */}
      {showLogoutSplash && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          }}
        >
          <LogoutSplash onComplete={handleLogoutComplete} />
        </Box>
      )}
    </AppBar>
  );
};

export default TopBar;